import { useMemo } from 'react';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { getEventUrl, getLessonPageUrl } from 'constants/pages';
import {
  type GetTopOpportunityEventsQuery,
  type GetTopOpportunityLessonsQuery,
  type SkillLevelValuesEnum,
  useGetTopOpportunityEventsQuery,
  useGetTopOpportunityLessonsQuery,
} from 'types/generated/client';
import { getActivityImageUrlOrDefault } from 'utils/shared/getActivityImageUrl';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { getEventSchedule } from 'utils/shared/time/getEventSchedule';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import type { FindFilters } from 'screens/Find/filters';
import { FIND_RADIUS_MILES, searchCenterFor } from 'screens/Find/geo';
import { useFindGames } from 'screens/Find/useFindGames';
import { useViewerCenter } from 'screens/Find/useViewerCenter';
import {
  HomeDateRangeEnum,
  HomeFilters,
} from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';
import {
  classifyEvent,
  classifyGame,
  classifyLesson,
  matchesIntent,
} from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/intent';
import {
  feedAreas,
  matchesActivitySheet,
  matchesGameSheet,
  venueWhitelistFilters,
} from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/sheetFilters';
import {
  OpportunityItem,
  TopOpportunityEvent,
} from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/types';

// One base radius for every feed source (DECISIONS-HOME-FEED.md §4); the sheet's
// travel-radius bonus is added on top in step 2.
export const FEED_RADIUS_MILES = FIND_RADIUS_MILES;
const ROW_LIMIT = 8;

// Rolling windows: Today = rest of today, week = next 7 days, month = next 30.
export const dateWindowFor = (range: HomeDateRangeEnum): { start: Date; end: Date } => {
  const now = new Date();
  if (range === HomeDateRangeEnum.Today) return { start: now, end: endOfDay(now) };
  if (range === HomeDateRangeEnum.ThisWeek) return { start: now, end: addDays(startOfDay(now), 7) };
  return { start: now, end: addDays(startOfDay(now), 30) };
};

const avatarUrlsOf = (
  rows: { userProfile?: { profileImageProviderUrl?: string | null } | null }[] | null | undefined,
) =>
  (rows ?? [])
    .map((row) => row.userProfile?.profileImageProviderUrl)
    .filter((url): url is string => Boolean(url));

const priceDisplayOf = (unitAmount: number, currency: string) =>
  unitAmount > 0 ? convertUnitPriceToFormattedPrice(unitAmount, currency).priceDisplay : undefined;

const mapEvent = (event: GetTopOpportunityEventsQuery['events'][number]): TopOpportunityEvent => ({
  id: event.id,
  href: getEventUrl(event.slug || event.id),
  title: event.title,
  sport: event.sport,
  type: event.type,
  isRfx: event.isRfx,
  intents: classifyEvent(event),
  startDateTime: getEventSchedule(event).current.startsAt,
  endDateTime: getEventSchedule(event).current.endsAt,
  timezone: resolveVenueTimeZone(event),
  address: event.addressString || event.displayLocation || undefined,
  venueId: event.venue?.id,
  venueName: event.venue?.title || event.displayLocation || undefined,
  latitude: event.latitude != null ? Number(event.latitude) : undefined,
  longitude: event.longitude != null ? Number(event.longitude) : undefined,
  ageGroup: event.intendedAgeGroup,
  priceMajorUnits: event.registrationPriceUnitAmount / 100,
  description: event.description || undefined,
  priceDisplay: priceDisplayOf(event.registrationPriceUnitAmount, event.currency),
  participantLimit: event.participantLimit,
  registrationCount: event.registrationsAggregate.aggregate?.count ?? 0,
  avatarUrls: avatarUrlsOf(event.registrations),
  coverImageUrl: getActivityImageUrlOrDefault(event),
  organizerName: event.hostUserProfile?.fullName || undefined,
});

const mapLesson = (
  lesson: GetTopOpportunityLessonsQuery['lessons'][number],
): TopOpportunityEvent => {
  // A lesson at a coach's typed-in court has no venue; the court stands in.
  const location = lesson.venue ?? lesson.userCustomCourt;
  return {
    id: lesson.id,
    href: lesson.externalBookingUrl?.trim() || getLessonPageUrl(lesson.id),
    title: lesson.title,
    sport: lesson.sport,
    type: lesson.type,
    intents: classifyLesson(lesson),
    startDateTime: lesson.startDateTime,
    endDateTime: lesson.endDateTime,
    timezone: resolveVenueTimeZone(lesson),
    address: lesson.venue?.addressString || lesson.userCustomCourt?.fullAddress || undefined,
    venueId: lesson.venue?.id,
    venueName: lesson.venue?.title || lesson.userCustomCourt?.title || undefined,
    latitude: location?.latitude != null ? Number(location.latitude) : undefined,
    longitude: location?.longitude != null ? Number(location.longitude) : undefined,
    ageGroup: lesson.intendedAgeGroup,
    skillLevels: lesson.skillLevels.map((level) => level.skillLevel as SkillLevelValuesEnum),
    priceMajorUnits: lesson.priceUnitAmount / 100,
    description: lesson.description || undefined,
    priceDisplay: priceDisplayOf(lesson.priceUnitAmount, lesson.currency),
    participantLimit: lesson.participantLimit,
    registrationCount: lesson.participantsAggregate.aggregate?.count ?? 0,
    avatarUrls: avatarUrlsOf(lesson.participants),
    coverImageUrl: getActivityImageUrlOrDefault(lesson),
    organizerName: lesson.ownerProfile?.fullName || undefined,
  };
};

export const useTopOpportunities = (filters: HomeFilters, sheet: FindFilters) => {
  // The feed searches the default market (or picked neighborhoods); the viewer's own
  // point only measures the distance shown on each card.
  const viewerCenter = useViewerCenter();

  const { start, end } = useMemo(
    () => dateWindowFor(filters.dateRange),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.dateRange],
  );

  const areas = useMemo(() => feedAreas(sheet), [sheet]);
  const searchCenter = useMemo(
    () => searchCenterFor(sheet.neighborhoods, viewerCenter),
    [sheet.neighborhoods, viewerCenter],
  );
  const eventArea = useMemo(() => ({ _or: areas.map((geometry) => ({ geometry })) }), [areas]);
  const lessonArea = useMemo(
    () => ({
      _or: areas.flatMap((geometry) => [
        { venue: { geometry } },
        { userCustomCourt: { geometry } },
      ]),
    }),
    [areas],
  );
  const whitelist = venueWhitelistFilters(sheet);
  const variables = {
    sport: filters.sport,
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };

  // Keep the last result on screen while a filter change refetches (no skeleton flash).
  const {
    data: eventsData,
    previousData: eventsPrev,
    loading: eventsLoading,
  } = useGetTopOpportunityEventsQuery({
    variables: {
      ...variables,
      area: eventArea,
      venueFilter: whitelist.events,
    },
    skip: areas.length === 0,
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: lessonsData,
    previousData: lessonsPrev,
    loading: lessonsLoading,
  } = useGetTopOpportunityLessonsQuery({
    variables: {
      ...variables,
      area: lessonArea,
      venueFilter: whitelist.lessons,
    },
    skip: areas.length === 0,
    fetchPolicy: 'cache-and-network',
  });
  const events = (eventsData ?? eventsPrev)?.events ?? [];
  const lessons = (lessonsData ?? lessonsPrev)?.lessons ?? [];

  // Pickup games come pre-fetched for the same areas; narrow client-side to the
  // bar's sport + date window.
  const { games, isLoading: gamesLoading } = useFindGames(viewerCenter, areas, searchCenter);

  return useMemo(() => {
    const intent = filters.intent;

    const activities = [...events.map(mapEvent), ...lessons.map(mapLesson)].filter(
      (activity) =>
        matchesIntent(activity.intents, intent) &&
        matchesActivitySheet(activity, sheet, filters.dateRange),
    );

    const gamesInWindow = games.filter((game) => {
      if (game.sport !== filters.sport) return false;
      if (!matchesIntent(classifyGame(game), intent)) return false;
      if (!matchesGameSheet(game, sheet, filters.dateRange)) return false;
      const startsAt = new Date(game.startDateTime);
      return startsAt >= start && startsAt <= end;
    });

    // Featured hero: the busiest upcoming activity in the intent's set.
    // @todo confirm selection with design.
    const featured = activities.reduce<TopOpportunityEvent | null>(
      (top, activity) =>
        activity.registrationCount > (top?.registrationCount ?? -1) ? activity : top,
      null,
    );

    const rows: OpportunityItem[] = [
      ...activities
        .filter((activity) => activity.id !== featured?.id)
        .map((event): OpportunityItem => ({ kind: 'event', sortDate: event.startDateTime, event })),
      ...gamesInWindow.map(
        (game): OpportunityItem => ({ kind: 'game', sortDate: game.startDateTime, game }),
      ),
    ]
      .sort((a, b) => new Date(a.sortDate).getTime() - new Date(b.sortDate).getTime())
      .slice(0, ROW_LIMIT);

    const isEmpty = !featured && rows.length === 0;

    return {
      featured,
      rows,
      isEmpty,
      loading: (eventsLoading || lessonsLoading || gamesLoading) && isEmpty,
    };
  }, [
    events,
    lessons,
    games,
    filters.sport,
    filters.intent,
    filters.dateRange,
    sheet,
    start,
    end,
    eventsLoading,
    lessonsLoading,
    gamesLoading,
  ]);
};
